import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Markdown from './Markdown';
import { extractHeadings } from '../../common/toc';
import { useActiveHeading } from '../../common/useActiveHeading';

// Webflow content27: markdown body with a table-of-contents sidebar —
// sticky on desktop, collapsible accordion above the content on mobile.
//
// The list is a "rail" (ocl_online#221, chosen from a mockup): a small
// uppercase label, then smaller, muted entries on a thin line down the left,
// so it reads as navigation rather than more body text. The section being
// read is marked in primary on the rail. Not in ocl-design-system yet; propose
// it there once it settles.
const TocLinks = ({ headings, active }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1, borderLeft: '2px solid', borderColor: 'surface.n90' }}>
    {headings.map(({ depth, text, id }) => {
      const current = id === active;
      return (
        <li key={id}>
          <Box
            component="a"
            href={`#${id}`}
            aria-current={current ? 'location' : undefined}
            sx={{
              display: 'block',
              // Sits over the rail, so the current entry's marker replaces
              // that stretch of the grey line.
              ml: '-2px',
              pl: 1.75 + depth * 1.5,
              borderLeft: '2px solid',
              borderColor: current ? 'primary.main' : 'transparent',
              color: current ? 'primary.main' : 'surface.light',
              fontWeight: current ? 600 : 400,
              fontSize: '0.875rem',
              lineHeight: 1.45,
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' },
            }}>
            {text}
          </Box>
        </li>
      );
    })}
  </Box>
);

const MarkdownWithToc = ({ body, tocTitle }) => {
  const { t } = useTranslation();
  const heading = tocTitle || t('common.toc_title');
  const headings = React.useMemo(() => (body ? extractHeadings(body) : []), [body]);
  const hasToc = headings.length > 1;
  const active = useActiveHeading(hasToc ? headings : []);

  if (body === null)
    return (
      <Box>
        <Skeleton height={28} />
        <Skeleton height={28} />
        <Skeleton height={28} width="60%" />
      </Box>
    );

  return (
    // minmax(0, 1fr), not 1fr: a 1fr track grows to fit its widest unbreakable
    // content, so a wide table or long URL would widen the page on a phone.
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: hasToc ? '260px minmax(0, 1fr)' : 'minmax(0, 1fr)' }, gap: { xs: 0, md: 5 }, alignItems: 'start' }}>
      {hasToc && (
        <>
          <Accordion disableGutters elevation={0} sx={{ display: { md: 'none' }, border: 1, borderColor: 'surface.n90', borderRadius: '8px', marginTop: '16px', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} id="toc-header" aria-controls="toc-content">
              <Typography sx={{ fontWeight: 600, color: 'surface.dark' }}>{heading}</Typography>
            </AccordionSummary>
            {/* MUI gives the details' region its id and aria-labelledby from the
                summary's aria-controls and id; repeating them here duplicated both. */}
            <AccordionDetails>
              <TocLinks headings={headings} />
            </AccordionDetails>
          </Accordion>
          <Box component="nav" aria-label={heading} sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 96, marginTop: '16px' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'surface.light', mb: 1.5 }}>
              {heading}
            </Typography>
            <TocLinks headings={headings} active={active} />
          </Box>
        </>
      )}
      <Markdown>{body}</Markdown>
    </Box>
  );
};

export default MarkdownWithToc;
